import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { format } from "date-fns";
import { ca } from "date-fns/locale";

export default function OvertimeExport({ venueId, records, month }) {
  const [exporting, setExporting] = useState(false);

  const exportCSV = () => {
    setExporting(true);

    const headers = [
      "Treballador", "Data", "Prevista (min)", "Treballada (min)", "Diferència (min)",
      "Classificació", "Estat", "Entrada", "Sortida", "Incidències", "Notes"
    ];

    const classLabels = {
      ordinari: "Ordinari",
      possible_complementaria: "Possible complementària",
      possible_extraordinaria: "Possible extraordinària",
      incidencia_fitxatge: "Incidència fitxatge",
      pendent_validacio: "Pendent validació",
    };

    const statusLabels = {
      detected: "Detectat",
      pending_review: "Pendent revisió",
      approved_as_overtime: "Aprovat extra",
      approved_as_complementary: "Aprovat complementària",
      rejected: "Rebutjat",
      compensated_with_rest: "Compensat descans",
      paid: "Pagat",
    };

    const rows = records.map(r => [
      r.user_name,
      r.date,
      r.planned_work_time,
      r.worked_time,
      r.time_difference,
      classLabels[r.classification] || r.classification,
      statusLabels[r.status] || r.status,
      r.entry_time ? format(new Date(r.entry_time), "HH:mm") : "",
      r.exit_time ? format(new Date(r.exit_time), "HH:mm") : "",
      (r.attendance_incidents || []).join(" | "),
      r.review_notes || "",
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `hores_extra_${month || "export"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setExporting(false);
  };

  // Resum per treballador
  const exportSummaryCSV = () => {
    const byWorker = {};
    records.forEach(r => {
      if (!byWorker[r.user_email]) {
        byWorker[r.user_email] = {
          name: r.user_name,
          email: r.user_email,
          totalPlanned: 0,
          totalWorked: 0,
          totalDiff: 0,
          overtimeDays: 0,
          incidentDays: 0,
          validated: 0,
        };
      }
      const w = byWorker[r.user_email];
      w.totalPlanned += r.planned_work_time || 0;
      w.totalWorked += r.worked_time || 0;
      w.totalDiff += r.time_difference || 0;
      if (r.overtime_detected || r.complementary_hours_detected) w.overtimeDays++;
      if ((r.attendance_incidents || []).length > 0) w.incidentDays++;
      if (!["detected", "pending_review"].includes(r.status)) w.validated++;
    });

    const headers = ["Treballador", "Email", "Min prevists", "Min treballats", "Diferència total (min)", "Dies excés", "Dies incidència", "Registres validats"];
    const rows = Object.values(byWorker).map(w => [
      w.name, w.email, w.totalPlanned, w.totalWorked, w.totalDiff, w.overtimeDays, w.incidentDays, w.validated
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `resum_hores_${month || "export"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={exportCSV} disabled={exporting} className="gap-1.5">
        <Download className="w-4 h-4" />
        Exportar detall
      </Button>
      <Button variant="outline" size="sm" onClick={exportSummaryCSV} className="gap-1.5">
        <Download className="w-4 h-4" />
        Resum mensual
      </Button>
    </div>
  );
}